import { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Images, LogOut } from "lucide-react";
import { adminApi, TOKEN_KEY } from "@/lib/adminApi";
import MediaStatusBar from "@/components/media/MediaStatusBar";
import MediaUploader from "@/components/media/MediaUploader";
import ThemeManager from "@/components/media/ThemeManager";
import MediaLibrary from "@/components/media/MediaLibrary";
import YoutubeForm from "@/components/media/YoutubeForm";

export default function AdminMediaPage() {
  const token = localStorage.getItem(TOKEN_KEY);
  const api = useMemo(() => adminApi(token), [token]);
  const [items, setItems] = useState([]);
  const [themes, setThemes] = useState([]);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, t, s] = await Promise.all([api.get("/media"), api.get("/themes"), api.get("/admin/media/status")]);
      setItems(m.data);
      setThemes(t.data.themes || []);
      setStatus(s.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        window.location.href = "/admin";
      } else toast.error("Chargement des médias impossible.");
    }
  }, [api]);

  useEffect(() => {
    if (!token) window.location.href = "/admin";
    else load();
  }, [token, load]);

  if (!token) return null;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = "/admin";
  };

  return (
    <div data-testid="admin-media-page" className="min-h-screen bg-[#FAF8F5] px-4 pb-24 pt-8 text-stone-800">
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <a data-testid="admin-media-back" href="/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-stone-500 transition-colors hover:text-[#C5A059]">
              <ArrowLeft className="h-3.5 w-3.5" /> Espace Famille
            </a>
            <h1 className="mt-3 flex items-center gap-3 font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
              <Images className="h-7 w-7 text-[#C5A059]" /> Photos & vidéos
            </h1>
            <p className="mt-1 text-sm text-stone-500">Déposez, classez, légendez et épinglez les souvenirs de l'album.</p>
          </div>
          <div className="flex gap-2">
            <a href="/galerie" data-testid="admin-media-view-gallery" className="rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-[#C5A059]">
              Voir la galerie
            </a>
            <button data-testid="admin-media-logout" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-xs font-semibold text-stone-600 transition-colors hover:border-[#C85A32] hover:text-[#C85A32]">
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </header>

        <MediaStatusBar api={api} items={items} status={status} onRefresh={load} />
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-6">
            <MediaUploader api={api} themes={themes} onDone={load} />
            <YoutubeForm api={api} themes={themes} onDone={load} />
          </div>
          <ThemeManager api={api} themes={themes} items={items} onChange={load} />
        </div>
        <MediaLibrary api={api} items={items} themes={themes} missing={status?.missing || []} onChange={load} />
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
